// RecessionDashboard.js - Recession Probability Dashboard Component
import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  TrendingDown, 
  TrendingUp, 
  Activity, 
  RefreshCw, 
  Info,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Target,
  Shield,
  Zap
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  ReferenceLine,
  ReferenceArea,
  Area,
  AreaChart
} from 'recharts';
import PageAwareAI from './PageAwareAI';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const RecessionDashboard = () => {
  const [data, setData] = useState(null);
  const [yieldCurveData, setYieldCurveData] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedIndicator, setExpandedIndicator] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [probResponse, yieldResponse, histResponse] = await Promise.all([
        fetch(`${API_URL}/recession/probability`),
        fetch(`${API_URL}/recession/yield-curve?years=20`),
        fetch(`${API_URL}/recession/historical`)
      ]);

      if (probResponse.ok) {
        const probData = await probResponse.json();
        setData(probData);
      }

      if (yieldResponse.ok) {
        const yieldData = await yieldResponse.json();
        setYieldCurveData(yieldData);
      }

      if (histResponse.ok) {
        const histData = await histResponse.json();
        setHistoricalData(histData);
      }
    } catch (error) {
      console.error('Failed to fetch recession data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetch(`${API_URL}/recession/refresh`, { method: 'POST' });
      await fetchAllData();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Probability Gauge Component - Enhanced Design
  const ProbabilityGauge = ({ probability }) => {
    const getGaugeColor = (prob) => {
      if (prob >= 70) return '#ef4444';
      if (prob >= 50) return '#f59e0b';
      if (prob >= 30) return '#eab308';
      return '#22c55e';
    };

    const color = getGaugeColor(probability);
    const rotation = (probability / 100) * 180 - 90;
    
    // Risk level text
    const getRiskLevel = (prob) => {
      if (prob >= 70) return 'High Risk';
      if (prob >= 50) return 'Elevated';
      if (prob >= 30) return 'Moderate';
      return 'Low Risk';
    };

    return (
      <div style={{ position: 'relative', width: '100%', maxWidth: '320px', margin: '0 auto' }}>
        {/* Main SVG Gauge */}
        <svg viewBox="0 0 200 120" style={{ width: '100%', height: 'auto' }}>
          <defs>
            {/* Gradient for the gauge arc */}
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#22c55e" />
              <stop offset="30%" stopColor="#84cc16" />
              <stop offset="50%" stopColor="#eab308" />
              <stop offset="70%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
            
            {/* Glow filter for needle */}
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            
            {/* Shadow for depth */}
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3"/>
            </filter>
          </defs>
          
          {/* Background arc (dark) */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="#1e293b"
            strokeWidth="24"
            strokeLinecap="round"
          />
          
          {/* Colored gradient arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="url(#gaugeGradient)"
            strokeWidth="20"
            strokeLinecap="round"
            filter="url(#shadow)"
          />
          
          {/* Tick marks */}
          {[0, 25, 50, 75, 100].map((tick, i) => {
            const angle = (tick / 100) * 180 - 180;
            const radian = (angle * Math.PI) / 180;
            const innerR = 60;
            const outerR = 68;
            const x1 = 100 + innerR * Math.cos(radian);
            const y1 = 100 + innerR * Math.sin(radian);
            const x2 = 100 + outerR * Math.cos(radian);
            const y2 = 100 + outerR * Math.sin(radian);
            return (
              <line
                key={tick}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#64748b"
                strokeWidth="2"
                strokeLinecap="round"
              />
            );
          })}
          
          {/* Labels */}
          <text x="28" y="108" fill="#64748b" fontSize="9" fontWeight="500">0%</text>
          <text x="92" y="25" fill="#64748b" fontSize="9" fontWeight="500">50%</text>
          <text x="164" y="108" fill="#64748b" fontSize="9" fontWeight="500">100%</text>
          
          {/* Center decorative circle */}
          <circle cx="100" cy="100" r="45" fill="#0f172a" stroke="#334155" strokeWidth="2" />
          <circle cx="100" cy="100" r="40" fill="#1e293b" />
          
          {/* Needle */}
          <g transform={`rotate(${rotation}, 100, 100)`} filter="url(#glow)">
            {/* Needle body */}
            <polygon
              points="100,35 96,95 100,100 104,95"
              fill={color}
            />
            {/* Needle tip glow */}
            <circle cx="100" cy="40" r="4" fill={color} opacity="0.6" />
          </g>
          
          {/* Center cap */}
          <circle cx="100" cy="100" r="12" fill="#334155" stroke={color} strokeWidth="3" />
          <circle cx="100" cy="100" r="6" fill={color} />
        </svg>
        
        {/* Probability display overlay */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -10%)',
          textAlign: 'center',
          pointerEvents: 'none'
        }}>
          <div style={{ 
            fontSize: 'clamp(2rem, 8vw, 3rem)', 
            fontWeight: 'bold', 
            color: color,
            lineHeight: 1,
            textShadow: `0 0 20px ${color}40`
          }}>
            {probability}%
          </div>
          <div style={{ 
            fontSize: '0.75rem', 
            color: '#94a3b8',
            marginTop: '0.25rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {getRiskLevel(probability)}
          </div>
        </div>
      </div>
    );
  };

  // Indicator Card Component
  const IndicatorCard = ({ id, indicator, isExpanded, onToggle }) => {
    const getTrendIcon = (trend) => {
      if (trend === 'rising') return <TrendingUp size={16} />;
      if (trend === 'falling') return <TrendingDown size={16} />;
      return <Activity size={16} />;
    };

    return (
      <div
        style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: `1px solid ${indicator.statusColor}40`,
          borderRadius: '0.75rem',
          overflow: 'hidden',
          transition: 'all 0.2s'
        }}
      >
        <div
          onClick={() => onToggle(id)}
          style={{
            padding: '1rem',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: indicator.statusColor,
                  boxShadow: `0 0 8px ${indicator.statusColor}`
                }}
              />
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#cbd5e1' }}>
                {indicator.name}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: indicator.statusColor }}>
                {indicator.displayValue}
              </span>
              <span style={{
                fontSize: '0.75rem',
                color: indicator.trend === 'rising' ? '#ef4444' : 
                       indicator.trend === 'falling' ? '#22c55e' : '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                {getTrendIcon(indicator.trend)}
                {indicator.trend}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              padding: '0.25rem 0.75rem',
              background: `${indicator.statusColor}20`,
              borderRadius: '9999px',
              fontSize: '0.75rem',
              fontWeight: '600',
              color: indicator.statusColor
            }}>
              {indicator.statusLabel}
            </div>
            <div style={{
              padding: '0.375rem',
              background: 'rgba(51, 65, 85, 0.5)',
              borderRadius: '0.375rem'
            }}>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div style={{
            padding: '1rem',
            borderTop: '1px solid #334155',
            background: 'rgba(15, 23, 42, 0.5)'
          }}>
            <p style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '1rem' }}>
              {indicator.description}
            </p>
            
            {/* Mini chart */}
            {indicator.history && indicator.history.length > 0 && (
              <div style={{ height: '100px', marginBottom: '1rem' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={[...indicator.history].reverse()}>
                    <defs>
                      <linearGradient id={`gradient-${id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={indicator.statusColor} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={indicator.statusColor} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={indicator.statusColor}
                      fill={`url(#gradient-${id})`}
                      strokeWidth={2}
                    />
                    <XAxis dataKey="date" hide />
                    <YAxis hide domain={['auto', 'auto']} />
                    <Tooltip
                      contentStyle={{
                        background: 'rgba(15, 23, 42, 0.95)',
                        border: '1px solid #334155',
                        borderRadius: '0.5rem'
                      }}
                      formatter={(value) => [value.toFixed(2), indicator.name]}
                      labelFormatter={(label) => new Date(label).toLocaleDateString()}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Thresholds */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '0.5rem',
              fontSize: '0.75rem'
            }}>
              <div style={{ padding: '0.5rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.375rem', textAlign: 'center' }}>
                <div style={{ color: '#22c55e', fontWeight: '600' }}>Safe</div>
                <div style={{ color: '#94a3b8' }}>{indicator.thresholds?.safe}</div>
              </div>
              <div style={{ padding: '0.5rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.375rem', textAlign: 'center' }}>
                <div style={{ color: '#f59e0b', fontWeight: '600' }}>Warning</div>
                <div style={{ color: '#94a3b8' }}>{indicator.thresholds?.warning}</div>
              </div>
              <div style={{ padding: '0.5rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.375rem', textAlign: 'center' }}>
                <div style={{ color: '#ef4444', fontWeight: '600' }}>Danger</div>
                <div style={{ color: '#94a3b8' }}>{indicator.thresholds?.danger}</div>
              </div>
            </div>

            {/* Weight contribution */}
            <div style={{ 
              marginTop: '1rem', 
              padding: '0.75rem', 
              background: 'rgba(59, 130, 246, 0.1)', 
              borderRadius: '0.5rem',
              fontSize: '0.75rem',
              color: '#93c5fd'
            }}>
              <strong>Model Weight:</strong> {(indicator.weight * 100).toFixed(0)}% | 
              <strong> Contribution:</strong> {indicator.weightedContribution} points
            </div>
          </div>
        )}
      </div>
    );
  };

  // Business Cycle Component
  const BusinessCycleCard = ({ cycle, probability }) => {
    if (!cycle) return null;

    const stages = ['Expansion', 'Peak', 'Contraction', 'Trough'];
    const currentIndex = stages.indexOf(cycle.name);

    return (
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem'
      }}>
        <h3 style={{ 
          fontSize: '1.125rem', 
          fontWeight: 'bold', 
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Target size={20} color="#60a5fa" />
          Business Cycle Stage
        </h3>

        {/* Cycle visualization */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '1.5rem',
          position: 'relative',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: '0.5rem'
        }}>
          {/* Connection line */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '30px',
            right: '30px',
            height: '2px',
            background: '#334155'
          }} />
          
          {stages.map((stage, idx) => {
            const isActive = idx === currentIndex;
            const stageColors = {
              Expansion: '#22c55e',
              Peak: '#f59e0b',
              Contraction: '#ef4444',
              Trough: '#8b5cf6'
            };
            
            return (
              <div key={stage} style={{ textAlign: 'center', position: 'relative', zIndex: 1, minWidth: '60px', flexShrink: 0 }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: isActive ? stageColors[stage] : '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.5rem',
                  boxShadow: isActive ? `0 0 20px ${stageColors[stage]}50` : 'none',
                  transition: 'all 0.3s'
                }}>
                  {isActive && <Zap size={16} color="white" />}
                </div>
                <div style={{
                  fontSize: '0.65rem',
                  fontWeight: isActive ? '600' : '400',
                  color: isActive ? stageColors[stage] : '#64748b'
                }}>
                  {stage}
                </div>
              </div>
            );
          })}
        </div>

        {/* Current stage details */}
        <div style={{
          padding: '1rem',
          background: `${cycle.color}15`,
          border: `1px solid ${cycle.color}40`,
          borderRadius: '0.5rem',
          marginBottom: '1rem'
        }}>
          <div style={{ 
            fontSize: '1.25rem', 
            fontWeight: 'bold', 
            color: cycle.color,
            marginBottom: '0.5rem'
          }}>
            {cycle.name} Phase
          </div>
          <p style={{ fontSize: '0.875rem', color: '#cbd5e1', margin: 0 }}>
            {cycle.description}
          </p>
        </div>

        {/* Recommendations */}
        <div>
          <div style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600', 
            color: '#cbd5e1',
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Shield size={16} color="#60a5fa" />
            General Considerations
            <span style={{ 
              fontSize: '0.65rem', 
              color: '#94a3b8', 
              fontWeight: '400',
              marginLeft: 'auto'
            }}>
              (Not financial advice)
            </span>
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {cycle.recommendations.map((rec, idx) => (
              <li key={idx} style={{ 
                fontSize: '0.875rem', 
                color: '#94a3b8',
                marginBottom: '0.5rem'
              }}>
                {rec}
              </li>
            ))}
          </ul>
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem',
            background: 'rgba(245, 158, 11, 0.1)',
            borderRadius: '0.375rem',
            fontSize: '0.65rem',
            color: '#fcd34d'
          }}>
            ⚠️ These are general observations about historical market behavior during similar economic conditions, 
            not personalized investment recommendations.
          </div>
        </div>
      </div>
    );
  };

  // Yield Curve Chart Component
  const YieldCurveChart = ({ data, recessions }) => {
    if (!data || data.length === 0) return null;

    const chartData = data.map(d => ({
      ...d,
      displayDate: new Date(d.date).toLocaleDateString('en-US', { 
        year: '2-digit', 
        month: 'short' 
      })
    }));

    return (
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1rem'
      }}>
        <h3 style={{ 
          fontSize: '1rem', 
          fontWeight: 'bold', 
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
          <BarChart3 size={18} color="#60a5fa" />
          <span>Yield Curve (10Y-2Y) - 20 Years</span>
        </h3>

        <div style={{ height: '280px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="yieldGradientPos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="yieldGradientNeg" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              
              <XAxis 
                dataKey="displayDate" 
                stroke="#64748b"
                tick={{ fill: '#64748b', fontSize: 10 }}
                interval="preserveStartEnd"
              />
              
              <YAxis 
                stroke="#64748b"
                tick={{ fill: '#64748b', fontSize: 10 }}
                domain={['auto', 'auto']}
                tickFormatter={(v) => `${v.toFixed(1)}%`}
              />
              
              {/* Zero line - inversion threshold */}
              <ReferenceLine 
                y={0} 
                stroke="#f59e0b" 
                strokeWidth={2}
                strokeDasharray="5 5"
                label={{ 
                  value: 'Inversion', 
                  position: 'right',
                  fill: '#f59e0b',
                  fontSize: 11
                }}
              />
              
              {/* Recession shading */}
              {recessions && recessions.map((recession, idx) => (
                <ReferenceArea
                  key={idx}
                  x1={recession.start}
                  x2={recession.end}
                  fill="#ef4444"
                  fillOpacity={0.1}
                />
              ))}
              
              <Tooltip
                contentStyle={{
                  background: 'rgba(15, 23, 42, 0.95)',
                  border: '1px solid #334155',
                  borderRadius: '0.5rem'
                }}
                formatter={(value) => [`${value.toFixed(2)}%`, 'Spread']}
                labelFormatter={(label) => label}
              />
              
              <Area
                type="monotone"
                dataKey="value"
                stroke="#60a5fa"
                strokeWidth={2}
                fill="url(#yieldGradientPos)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div style={{ 
          display: 'flex', 
          gap: '1.5rem', 
          marginTop: '1rem',
          fontSize: '0.75rem',
          color: '#94a3b8',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '20px', height: '3px', background: '#60a5fa' }} />
            Yield Spread
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '20px', height: '3px', background: '#f59e0b', borderStyle: 'dashed' }} />
            Inversion Line (0%)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '20px', height: '12px', background: 'rgba(239, 68, 68, 0.2)' }} />
            Recession Periods
          </div>
        </div>

        {/* Info box */}
        <div style={{ 
          marginTop: '1rem', 
          padding: '0.75rem', 
          background: 'rgba(59, 130, 246, 0.1)', 
          borderRadius: '0.5rem',
          fontSize: '0.75rem',
          color: '#93c5fd'
        }}>
          <strong>💡 Key Insight:</strong> When the yield curve inverts (goes below 0%), 
          it has historically predicted a recession within 6-18 months. The yield curve 
          has successfully predicted every U.S. recession since 1970.
        </div>
      </div>
    );
  };

  // Historical Recessions Table
  const HistoricalRecessions = ({ data }) => {
    if (!data) return null;

    return (
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem'
      }}>
        <h3 style={{ 
          fontSize: '1.125rem', 
          fontWeight: 'bold', 
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AlertTriangle size={20} color="#f59e0b" />
          Historical U.S. Recessions
        </h3>

        {/* Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            padding: '1rem', 
            background: 'rgba(51, 65, 85, 0.5)', 
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#60a5fa' }}>
              {data.stats.totalRecessions}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Since 1970</div>
          </div>
          <div style={{ 
            padding: '1rem', 
            background: 'rgba(51, 65, 85, 0.5)', 
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f59e0b' }}>
              {data.stats.averageDuration}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Avg Duration</div>
          </div>
        </div>

        {/* Recession list */}
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {data.recessions.slice(0, 6).map((recession, idx) => (
            <div 
              key={idx}
              style={{
                padding: '1rem',
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}
            >
              <div>
                <div style={{ fontWeight: '600', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                  {recession.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {new Date(recession.start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - 
                  {new Date(recession.end).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </div>
              </div>
              <div style={{
                padding: '0.25rem 0.75rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                color: '#fca5a5'
              }}>
                {recession.duration}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Activity size={48} color="#60a5fa" className="spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Prominent Disclaimer Banner - Always Visible */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '0.5rem',
        padding: '0.75rem 1rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap'
      }}>
        <AlertTriangle size={18} color="#fca5a5" style={{ flexShrink: 0 }} />
        <p style={{ 
          margin: 0, 
          fontSize: '0.75rem', 
          color: '#fcd34d', 
          flex: 1,
          lineHeight: '1.4'
        }}>
          <strong>Educational Tool Only:</strong> This dashboard displays publicly available Federal Reserve data 
          for informational purposes. It does not constitute financial advice or recommendations to buy/sell securities. 
          Consult a qualified financial advisor before making investment decisions.
        </p>
      </div>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%)',
        borderRadius: '0.75rem',
        padding: '1.25rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h2 style={{ 
              fontSize: 'clamp(1.25rem, 4vw, 2rem)', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap'
            }}>
              <AlertTriangle size={24} />
              Recession Dashboard
            </h2>
            <p style={{ color: '#e0e7ff', fontSize: '0.875rem', margin: 0 }}>
              Economic analysis using Federal Reserve data
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.75rem',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1.5rem',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '0.5rem',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        {['overview', 'indicators', 'yield-curve', 'history'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.625rem 1rem',
              background: activeTab === tab ? '#3b82f6' : 'rgba(51, 65, 85, 0.5)',
              border: 'none',
              borderRadius: '0.5rem',
              color: activeTab === tab ? 'white' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'all 0.2s'
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1).replace('-', ' ')}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && data && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* Top row - Gauge and Interpretation */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '1rem' 
          }}>
            {/* Probability Gauge */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: '1.5rem'
            }}>
              <ProbabilityGauge probability={data.probability} />
              
              {/* Interpretation */}
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: `${data.interpretation.level === 'Very High' || data.interpretation.level === 'Elevated' 
                  ? 'rgba(239, 68, 68, 0.1)' 
                  : data.interpretation.level === 'Moderate'
                  ? 'rgba(245, 158, 11, 0.1)'
                  : 'rgba(34, 197, 94, 0.1)'}`,
                borderRadius: '0.5rem'
              }}>
                <div style={{ 
                  fontWeight: '600', 
                  marginBottom: '0.5rem',
                  color: data.interpretation.level === 'Very High' || data.interpretation.level === 'Elevated'
                    ? '#fca5a5'
                    : data.interpretation.level === 'Moderate'
                    ? '#fcd34d'
                    : '#86efac'
                }}>
                  {data.interpretation.level} Risk
                </div>
                <p style={{ fontSize: '0.875rem', color: '#cbd5e1', margin: '0 0 0.5rem 0' }}>
                  {data.interpretation.summary}
                </p>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>
                  <strong>Recommended Action:</strong> {data.interpretation.action}
                </p>
              </div>
            </div>

            {/* Business Cycle */}
            <BusinessCycleCard cycle={data.businessCycle} probability={data.probability} />
          </div>

          {/* Quick Indicator Summary */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem'
          }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Key Indicators at a Glance
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
              gap: '1rem' 
            }}>
              {Object.entries(data.indicators).map(([id, indicator]) => (
                <div 
                  key={id}
                  style={{
                    padding: '1rem',
                    background: 'rgba(15, 23, 42, 0.5)',
                    borderRadius: '0.5rem',
                    borderLeft: `4px solid ${indicator.statusColor}`
                  }}
                >
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                    {indicator.name}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: indicator.statusColor }}>
                    {indicator.displayValue}
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.25rem' }}>
                    {indicator.statusLabel}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Model explanation */}
          <div style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '0.75rem',
            padding: '1rem'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Info size={20} color="#60a5fa" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ flex: 1, minWidth: '200px' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#93c5fd', fontSize: '0.9rem' }}>
                  How This Model Works
                </h4>
                <p style={{ fontSize: '0.75rem', color: '#cbd5e1', margin: '0 0 0.75rem 0', lineHeight: '1.5' }}>
                  {data.modelExplanation.description}. {data.modelExplanation.accuracy}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                  {data.modelExplanation.weights.map((w, idx) => (
                    <span 
                      key={idx}
                      style={{
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(59, 130, 246, 0.2)',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        color: '#93c5fd'
                      }}
                    >
                      {w.indicator}: {w.weight}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Indicators Tab */}
      {activeTab === 'indicators' && data && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {Object.entries(data.indicators).map(([id, indicator]) => (
            <IndicatorCard
              key={id}
              id={id}
              indicator={indicator}
              isExpanded={expandedIndicator === id}
              onToggle={(id) => setExpandedIndicator(expandedIndicator === id ? null : id)}
            />
          ))}
        </div>
      )}

      {/* Yield Curve Tab */}
      {activeTab === 'yield-curve' && (
        <YieldCurveChart 
          data={yieldCurveData?.yieldCurve} 
          recessions={yieldCurveData?.recessions}
        />
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <HistoricalRecessions data={historicalData} />
      )}

      {/* Last updated */}
      {data && (
        <div style={{ 
          marginTop: '2rem', 
          textAlign: 'center', 
          fontSize: '0.75rem', 
          color: '#64748b' 
        }}>
          Last updated: {new Date(data.lastUpdated).toLocaleString()}
          {data.cached && ' (cached)'}
        </div>
      )}

      {/* Data Source Attribution */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: '0.5rem'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          marginBottom: '0.5rem'
        }}>
          <Info size={16} color="#60a5fa" />
          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#93c5fd' }}>
            Data Source
          </span>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0, lineHeight: '1.5' }}>
          All economic data displayed on this page is sourced from the{' '}
          <a 
            href="https://fred.stlouisfed.org/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#60a5fa', textDecoration: 'underline' }}
          >
            Federal Reserve Economic Data (FRED)
          </a>
          , a publicly available database maintained by the Federal Reserve Bank of St. Louis. 
          Data is updated according to each indicator's official release schedule.
        </p>
      </div>

      {/* Important Disclaimers */}
      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: '0.75rem'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          marginBottom: '0.75rem'
        }}>
          <AlertTriangle size={18} color="#fca5a5" />
          <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#fca5a5' }}>
            Important Disclaimers
          </span>
        </div>
        
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {/* Not Financial Advice */}
          <div style={{ 
            padding: '1rem', 
            background: 'rgba(15, 23, 42, 0.5)', 
            borderRadius: '0.5rem',
            borderLeft: '4px solid #ef4444'
          }}>
            <h4 style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              color: '#fca5a5', 
              margin: '0 0 0.5rem 0' 
            }}>
              🚫 Not Financial Advice
            </h4>
            <p style={{ fontSize: '0.75rem', color: '#cbd5e1', margin: 0, lineHeight: '1.6' }}>
              The information presented on this page is for <strong>educational and informational purposes only</strong>. 
              Nothing on this page constitutes financial advice, investment advice, trading advice, or any other 
              form of professional advice. This tool does not provide recommendations to buy, sell, or hold 
              any securities, investments, or financial instruments.
            </p>
          </div>

          {/* No Buy/Sell Recommendations */}
          <div style={{ 
            padding: '1rem', 
            background: 'rgba(15, 23, 42, 0.5)', 
            borderRadius: '0.5rem',
            borderLeft: '4px solid #f59e0b'
          }}>
            <h4 style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              color: '#fcd34d', 
              margin: '0 0 0.5rem 0' 
            }}>
              📊 Probability Estimates Only
            </h4>
            <p style={{ fontSize: '0.75rem', color: '#cbd5e1', margin: 0, lineHeight: '1.6' }}>
              The recession probability and business cycle analysis shown are <strong>statistical estimates</strong> based 
              on historical patterns and publicly available economic data. These probabilities are not predictions 
              or guarantees of future economic conditions. Past performance and historical correlations do not 
              guarantee future results. Economic conditions can change rapidly and unpredictably.
            </p>
          </div>

          {/* Consult Professionals */}
          <div style={{ 
            padding: '1rem', 
            background: 'rgba(15, 23, 42, 0.5)', 
            borderRadius: '0.5rem',
            borderLeft: '4px solid #22c55e'
          }}>
            <h4 style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              color: '#86efac', 
              margin: '0 0 0.5rem 0' 
            }}>
              👨‍💼 Consult a Professional
            </h4>
            <p style={{ fontSize: '0.75rem', color: '#cbd5e1', margin: 0, lineHeight: '1.6' }}>
              Before making any investment or financial decisions, you should consult with a 
              <strong> qualified financial advisor, licensed investment professional, or other appropriate expert</strong> who 
              can evaluate your individual circumstances, risk tolerance, and financial goals. Never make 
              investment decisions based solely on information from this or any other online tool.
            </p>
          </div>

          {/* Model Limitations */}
          <div style={{ 
            padding: '1rem', 
            background: 'rgba(15, 23, 42, 0.5)', 
            borderRadius: '0.5rem',
            borderLeft: '4px solid #8b5cf6'
          }}>
            <h4 style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              color: '#c4b5fd', 
              margin: '0 0 0.5rem 0' 
            }}>
              ⚠️ Model Limitations
            </h4>
            <p style={{ fontSize: '0.75rem', color: '#cbd5e1', margin: 0, lineHeight: '1.6' }}>
              This analysis uses a simplified model based on select economic indicators. The model has inherent 
              limitations and cannot account for all factors that influence economic cycles, including geopolitical 
              events, policy changes, natural disasters, pandemics, or other unforeseen circumstances. The 
              weighting of indicators is based on historical analysis and may not reflect future conditions.
            </p>
          </div>

          {/* No Liability */}
          <div style={{ 
            padding: '1rem', 
            background: 'rgba(15, 23, 42, 0.5)', 
            borderRadius: '0.5rem',
            borderLeft: '4px solid #64748b'
          }}>
            <h4 style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600', 
              color: '#94a3b8', 
              margin: '0 0 0.5rem 0' 
            }}>
              📜 Limitation of Liability
            </h4>
            <p style={{ fontSize: '0.75rem', color: '#cbd5e1', margin: 0, lineHeight: '1.6' }}>
              InvestorQ, Offbeet Media and Tech Inc., and its affiliates shall not be held liable for any 
              losses, damages, or costs arising from the use of this tool or reliance on the information 
              provided. Users assume full responsibility for their own investment decisions. By using this 
              tool, you acknowledge and accept these terms.
            </p>
          </div>
        </div>

        {/* Final Summary */}
        <div style={{ 
          marginTop: '1rem', 
          padding: '0.75rem', 
          background: 'rgba(239, 68, 68, 0.15)', 
          borderRadius: '0.5rem',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '0.75rem', color: '#fca5a5', margin: 0, fontWeight: '500' }}>
            ⚡ This tool is designed to help you understand economic indicators and their historical relationships 
            to recessions. It is NOT a crystal ball and should NOT be used as the sole basis for any financial decision.
          </p>
        </div>
      </div>
   <PageAwareAI 
  pageId="recession"
  pageData={{
    indicators: data?.indicators,
    probability: data?.probability,
    yieldCurve: yieldCurveData,
    historicalData: historicalData,
    activeTab: activeTab
  }}
/>
    </div>
  );
};

export default RecessionDashboard;